'use client';

import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Visual weight of the row. Drives the border/background tint and the leading
 * icon color:
 * - `neutral`  — plain card row (e.g. an integration's "not connected yet" state).
 * - `emphasis` — primary-tinted, for the recommended / fastest path.
 * - `subtle`   — muted, for a low-priority secondary pointer.
 */
export type CalloutTone = 'neutral' | 'emphasis' | 'subtle';

export interface CalloutRowProps {
  /** Leading lucide icon. */
  icon: LucideIcon;
  /** Short, scannable heading. */
  title: React.ReactNode;
  /** One-line supporting copy under the title. */
  description?: React.ReactNode;
  /** Visual weight — see {@link CalloutTone}. Defaults to `neutral`. */
  tone?: CalloutTone;
  /**
   * When true the icon sits inside a bordered square (matches the resume intake
   * tiles); when false it's a bare icon aligned to the title. Defaults to the
   * bare icon, which suits the tinted `emphasis`/`subtle` callouts.
   */
  boxedIcon?: boolean;
  /** Trailing control — typically a Button. Rendered on the right (or below on mobile). */
  action?: React.ReactNode;
  className?: string;
}

const TONE_CONTAINER: Record<CalloutTone, string> = {
  neutral: 'border-border bg-card',
  emphasis: 'border-primary/30 bg-primary/5',
  subtle: 'border-border bg-muted/20',
};

const TONE_ICON: Record<CalloutTone, string> = {
  neutral: 'text-muted-foreground',
  emphasis: 'text-primary',
  subtle: 'text-muted-foreground',
};

/**
 * A bordered callout row: leading icon + title/description on the left, an
 * optional action on the right (stacking below on mobile). One primitive shared
 * by the Gmail "no account connected" empty state and the onboarding "prefill
 * from resume" hints, so the two stay visually in sync.
 */
export function CalloutRow({
  icon: Icon,
  title,
  description,
  tone = 'neutral',
  boxedIcon = false,
  action,
  className,
}: CalloutRowProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 border p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4',
        TONE_CONTAINER[tone],
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        {boxedIcon ? (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-border bg-background">
            <Icon className={cn('h-4 w-4', TONE_ICON[tone])} />
          </span>
        ) : (
          <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', TONE_ICON[tone])} />
        )}
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-label font-semibold text-foreground">{title}</p>
          {description ? (
            <p className="text-caption text-muted-foreground">{description}</p>
          ) : null}
        </div>
      </div>
      {action ? <div className="shrink-0 self-start sm:self-auto">{action}</div> : null}
    </div>
  );
}
